import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { ClockIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import Timestamp from 'app/frontend/base/components/Timestamp'
import PremiumUpsell from 'app/frontend/base/components/PremiumUpsell'
import { WCL_REPORT_RETRIEVAL_THRESHOLD } from 'app/frontend/base/config/wcl'
dayjs.extend(RelativeTime)

const formatDuration = (startTime: number, endTime: number) => {
  const durationInMinutes = Math.floor((endTime - startTime) / 60 / 1000)

  if (durationInMinutes >= 60) {
    const hours = Math.round(durationInMinutes / 60)
    return `${hours}h`
  }

  return `${durationInMinutes}m`
}

const RecentReportsDialog = ({
  reports,
  style,
  team,
  guild,
}: {
  reports: wowaudit.CharacterDetails['recentReports']
  style?: React.CSSProperties
  team?: wowaudit.Team
  guild?: wowaudit.Guild
}) => {
  const hasEnoughPremiumPoints =
    !guild || guild.premiumPoints >= WCL_REPORT_RETRIEVAL_THRESHOLD

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant='outline'
          size='xs'
          className='flex items-center gap-2'
          style={style}
        >
          <ClockIcon size={12} className='text-muted-foreground' />
          <span>Recent reports</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className='mb-0'>Recent reports</DialogTitle>
        </DialogHeader>

        {!hasEnoughPremiumPoints && team ? (
          <PremiumUpsell
            title='Premium feature'
            description='Warcraft Logs data can only be retrieved for teams with enough premium benefits.'
            to={`${team.path}/premium`}
          />
        ) : reports && reports.length > 0 ? (
          <div className='flex flex-col gap-2'>
            {reports.map((report) => (
              <Link
                key={report.code}
                to={`https://www.warcraftlogs.com/reports/${report.code}`}
                target='_blank'
                rel='noreferrer'
                className={`flex items-center gap-2 bg-card border rounded-md pr-1 hover:bg-card-nested`}
              >
                <img
                  src={
                    report.icon_url ??
                    'https://data.wowaudit.com/img/wcl-logo.png'
                  }
                  className='w-10 h-10 rounded-l-md'
                />
                <div className='grow'>
                  <span>{report.title}</span>
                  <div className='flex items-center gap-1.5 text-xs'>
                    <Timestamp
                      time={report.endTime}
                      markOutdatedDays={null}
                      withTooltip={true}
                    />

                    <span>-</span>

                    {report.guild_name ? (
                      <p className=''>{report.guild_name}</p>
                    ) : (
                      <p className='text-muted-foreground italic'>Personal</p>
                    )}
                  </div>
                </div>

                <div className='flex items-center gap-1.5 mr-1 text-muted-foreground text-sm'>
                  <span>
                    {formatDuration(report.startTime, report.endTime)}
                  </span>
                  <ClockIcon size={14} />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className='text-muted-foreground'>No recent reports found.</p>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default RecentReportsDialog
